import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import AdminTable from "common/AdminTable";
import {
    getRequest,
    postRequest,
    patchRequest,
    deleteRequest,
} from "apis/ApiInstence";
import useCities from "hooks/useCities";

const MinOrderList = () => {
    const [rules, setRules] = useState([]);
    const { cities } = useCities();
    const [selectedCity, setSelectedCity] = useState("");
    const [amount, setAmount] = useState("");
    const [saving, setSaving] = useState(false);

    const loadRules = async () => {
        try {
            const res = await getRequest("minorder");
            setRules(Array.isArray(res?.data) ? res.data : []);
        } catch (error) {
            toast.error("Failed to load minimum order rules!");
        }
    };

    useEffect(() => {
        loadRules();
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();

        if (!selectedCity || amount === "") {
            toast.error("Please select a city and enter an amount.");
            return;
        }

        setSaving(true);
        try {
            await postRequest("minorder", {
                city: selectedCity,
                min_order_amount: Number(amount),
            });
            toast.success("Minimum order amount saved!");
            setSelectedCity("");
            setAmount("");
            loadRules();
        } catch (error) {
            toast.error("Failed to save minimum order amount!");
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (rule) => {
        try {
            await patchRequest(`minorder/${rule.id}`, {
                is_active: !rule.is_active,
            });
            loadRules();
        } catch (error) {
            toast.error("Failed to update rule!");
        }
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This will remove the city's minimum order rule; it will fall back to the default.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await deleteRequest(`minorder/${id}`);
                    Swal.fire("Deleted!", "The rule has been removed.", "success");
                    loadRules();
                } catch (error) {
                    toast.error("Failed to delete rule!");
                }
            }
        });
    };

    const tableHeaders = [
        { Header: "City", accessor: "city" },
        { Header: "Minimum Order (₹)", accessor: "min_order_amount" },
        {
            Header: "Active",
            accessor: "is_active",
            Cell: ({ row }) => (
                <button
                    type="button"
                    className={
                        row.original.is_active
                            ? "badge badge-success border-0"
                            : "badge badge-danger border-0"
                    }
                    style={{ cursor: "pointer" }}
                    onClick={() => handleToggleActive(row.original)}
                >
                    {row.original.is_active ? "Active" : "Inactive"}
                </button>
            ),
        },
        {
            Header: "Actions",
            accessor: "actions",
            Cell: ({ row }) => (
                <button
                    className="btn removebtn mx-1 my-1"
                    onClick={() => handleDelete(row.original.id)}
                >
                    Remove
                </button>
            ),
        },
    ];

    return (
        <div className="container-fluid mt-5">
            <div className="mainheadig mx-4">
                <h4 className="text-white font-weight-bold">City Minimum Order</h4>
            </div>

            <form onSubmit={handleSave} className="row mx-4 mt-4 align-items-end">
                <div className="col-sm-4">
                    <label className="lables" htmlFor="minOrderCity">City</label>
                    <select
                        id="minOrderCity"
                        className="form-control"
                        value={selectedCity}
                        onChange={(e) => setSelectedCity(e.target.value)}
                    >
                        <option value="">Select city...</option>
                        {cities.map((city) => (
                            <option key={city} value={city}>{city}</option>
                        ))}
                    </select>
                </div>
                <div className="col-sm-4">
                    <label className="lables" htmlFor="minOrderAmount">Minimum Order Amount (₹)</label>
                    <input
                        id="minOrderAmount"
                        type="number"
                        min="0"
                        className="form-control"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="e.g. 350"
                    />
                </div>
                <div className="col-sm-4">
                    <button className="btn addbtn" disabled={saving}>
                        Save
                    </button>
                </div>
            </form>

            <div className="mx-4 mt-5">
                {rules.length > 0 ? (
                    <AdminTable tableHeaders={tableHeaders} tableData={rules} />
                ) : (
                    <p className="mt-3">No city-specific rules yet — every city uses the default minimum order amount.</p>
                )}
            </div>
        </div>
    );
};

export default MinOrderList;
